import { useVerifyOrganizationCreateToken } from '@/hooks/organization/use-verify-organization-create-token';
import { useVerifySignupInviteToken } from '@/hooks/organization/use-verify-signup-invite-token';

type SignupTokenValidationArgs = {
  orgCreateToken: string | null;
  signupToken: string | null;
};

type SignupTokenValidationResult =
  | { type: 'invalid' }
  | { type: 'token'; isLoading: boolean; orgCreateToken: string }
  | {
      type: 'invite';
      isLoading: boolean;
      signupToken: string;
      email: string;
    };

export function useOrganizationValidation(
  args: SignupTokenValidationArgs
): SignupTokenValidationResult {
  const { orgCreateToken, signupToken } = args;

  // Call hooks unconditionally to satisfy React Hook rules; they no-op when token is null
  const {
    valid: tokenValid,
    isLoading: tokenLoading,
    error: tokenError,
  } = useVerifyOrganizationCreateToken(orgCreateToken);
  const {
    valid: inviteValid,
    isLoading: inviteLoading,
    email: inviteEmail,
  } = useVerifySignupInviteToken(signupToken);

  // 1) Validate invalidity upfront
  if ((orgCreateToken && signupToken) || (!orgCreateToken && !signupToken)) {
    if (orgCreateToken && signupToken) {
      console.error('[useOrganizationValidation] both_provided');
    } else {
      console.error('[useOrganizationValidation] both_missing');
    }
    return { type: 'invalid' };
  }

  // Case: orgCreateToken is provided
  if (orgCreateToken) {
    if (!tokenLoading && (tokenError || !tokenValid)) {
      console.error(
        `[useOrganizationValidation] ${tokenError ? 'token_error' : 'token_invalid'}`
      );
      return { type: 'invalid' };
    }
    return { type: 'token', isLoading: tokenLoading, orgCreateToken };
  }

  // Case: invite signupToken is provided
  if (signupToken) {
    if (!inviteValid && !inviteLoading) return { type: 'invalid' };
    return {
      type: 'invite',
      isLoading: inviteLoading,
      signupToken,
      email: inviteEmail ?? '',
    };
  }
  return { type: 'invalid' };
}
